import BusinessSetupPage from './pages/setup/BusinessSetupPage'
import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigationType, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/components/auth/AuthContext'
import { ThemeProvider } from '@/components/theme/ThemeProvider'
import { ToastProvider } from '@/components/layout/Toast'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { RequireAdjustSettings } from '@/components/auth/RequireAdjustSettings'
import { RequireViewReports } from '@/components/auth/RequireViewReports'
import { RouteFallback } from '@/components/layout/RouteFallback'
// Eager on purpose (PERF-13): the first-paint auth path. Everything below is a
// route-level lazy chunk — most importantly the entire /admin tree, which no
// contractor session should ever download.
import AuthenticatedLayout from '@/pages/authenticated/layout'
import LoginPage from '@/pages/login/page'
const ResetPasswordPage = lazy(() => import('@/pages/reset-password/page'))
const SsoCallbackPage = lazy(() => import('@/pages/sso-callback/page'))
const SettingsOverviewPage = lazy(() => import('@/pages/authenticated/settings/overview/page'))
const SettingsPage = lazy(() => import('@/pages/authenticated/settings/page'))
const PhoneAssistantPage = lazy(() => import('@/pages/authenticated/settings/phone-assistant/page'))
const CallHandlingPage = lazy(() => import('@/pages/authenticated/settings/call-handling/page'))
const PaymentsPage = lazy(() => import('@/pages/authenticated/settings/payments/page'))
const InvoiceSettingsPage = lazy(() => import('@/pages/authenticated/settings/invoices/page'))
const ServicesPage = lazy(() => import('@/pages/authenticated/settings/services/page'))
const MemoryPage = lazy(() => import('@/pages/authenticated/settings/memory/page'))
const PreferencesPage = lazy(() => import('@/pages/authenticated/settings/preferences/page'))
const TeamPage = lazy(() => import('@/pages/authenticated/settings/team/page'))
const BillingPage = lazy(() => import('@/pages/authenticated/settings/billing/page'))
const BirthdaysPage = lazy(() => import('@/pages/authenticated/settings/birthdays/page'))
const ReviewsPage = lazy(() => import('@/pages/authenticated/settings/reviews/page'))
const SubscriptionsSettingsPage = lazy(() => import('@/pages/authenticated/settings/subscriptions/page'))
const SecurityPage = lazy(() => import('@/pages/authenticated/settings/security/page'))
const UpgradePage = lazy(() => import('@/pages/authenticated/upgrade/page'))
const UpgradeCheckoutPage = lazy(() => import('@/pages/authenticated/upgrade/checkout/page'))
const InvoicesPage = lazy(() => import('@/pages/authenticated/invoices/page'))
const InvoiceDetailPage = lazy(() => import('@/pages/authenticated/invoices/[id]/page'))
const JobsPage = lazy(() => import('@/pages/authenticated/jobs/page'))
const HistoryPage = lazy(() => import('@/pages/authenticated/history/page'))
// Legacy URL — the retired generate-invoice builder now create-or-finds the
// job's draft and lands in the unified invoice builder.
const GenerateInvoiceRedirect = lazy(() => import('@/pages/authenticated/jobs/GenerateInvoiceRedirect'))
const ConversationsPage = lazy(() => import('@/pages/authenticated/conversations/page'))
// Meetings workspace (plans/team-chat-and-meetings.md) — Google Meet meetings,
// transcripts, AI summaries. Deliberately no rail/tab-bar entry: reachable via
// ⌘K, search, the calendar chips, and team-chat meeting cards.
const MeetingsPage = lazy(() => import('@/pages/authenticated/meetings/page'))
const ReportsPage = lazy(() => import('@/pages/authenticated/reports/page'))
const NotificationsListPage = lazy(() => import('@/pages/authenticated/notifications/page'))
const RemindersPage = lazy(() => import('@/pages/authenticated/reminders/page'))
const CalendarPage = lazy(() => import('@/pages/authenticated/calendar/page'))
// New-IA pages — the portal's primary surfaces since the 2026-07 cutover
// (legacy /dashboard, /customers, /jobs, /payouts now redirect here).
const HomePage = lazy(() => import('@/pages/authenticated/home/page'))
const QueuePage = lazy(() => import('@/pages/authenticated/queue/page'))
const CallsPage = lazy(() => import('@/pages/authenticated/calls/page'))
const AriaPage = lazy(() => import('@/pages/authenticated/aria/page'))
// Forms workspace (plans/forms-workspace.md) — contractor forms library +
// builder; /f/:publicId is the published standalone form's public page.
const FormsPage = lazy(() => import('@/pages/authenticated/forms/page'))
const FormBuilderPage = lazy(() => import('@/pages/authenticated/forms/[id]/page'))
const PublicFormPage = lazy(() => import('@/pages/public/form/page'))
const MoneyPage = lazy(() => import('@/pages/authenticated/money/page'))
const CustomersV2Page = lazy(() => import('@/pages/authenticated/customers-v2/page'))
const CustomerDetailPage = lazy(() => import('@/pages/authenticated/customers-v2/[id]/page'))
const BookingPage = lazy(() => import('@/pages/public/booking/page'))
const MeetingRsvpPage = lazy(() => import('@/pages/public/meeting-rsvp/page'))
const SampleBookingPage = lazy(() => import('@/pages/public/booking/sample'))
const SampleSetupPage = lazy(() => import('@/pages/public/setup/sample'))
const DemoQueuePage = lazy(() => import('@/pages/public/demo/queue/page'))
const DemoFollowupPage = lazy(() => import('@/pages/public/demo/followup/page'))
const DemoSampleInvoicePage = lazy(() => import('@/pages/public/demo/sample-invoice/page'))
const DemoSamplePaymentPage = lazy(() => import('@/pages/public/demo/sample-payment/page'))
const SecureAccountPage = lazy(() => import('@/pages/secure-account/page'))
const SignupPage = lazy(() => import('@/pages/signup/page'))
const OutreachSignupPage = lazy(() => import('@/pages/public/outreach-signup/page'))
const PaySuccessPage = lazy(() => import('@/pages/public/pay/success'))
const PayCancelPage = lazy(() => import('@/pages/public/pay/cancel'))
const ReviewRedirectPage = lazy(() => import('@/pages/public/review-redirect/page'))
const PrivacyPolicyPage = lazy(() => import('@/pages/public/legal/privacy'))
const TermsOfServicePage = lazy(() => import('@/pages/public/legal/terms'))
const AcceptableUsePolicyPage = lazy(() => import('@/pages/public/legal/aup'))
const DataProcessingAddendumPage = lazy(() => import('@/pages/public/legal/dpa'))
const SmsDemoConsentPage = lazy(() => import('@/pages/public/legal/sms-demo-consent'))
const SubscribePage = lazy(() => import('@/pages/public/subscribe/page'))
// Named-export page: wrap into a default for lazy().
const SubscribeSuccessPage = lazy(() =>
  import('@/pages/public/subscribe/page').then((m) => ({ default: m.SubscribeSuccessPage })),
)
// Admin — the whole console (layout included) is a separate chunk family.
const AdminLayout = lazy(() => import('@/pages/admin/layout'))
const AdminDashboardPage = lazy(() => import('@/pages/admin/page'))
const ControlTowerPage = lazy(() => import('@/pages/admin/control-tower/page'))
const PersonaPage = lazy(() => import('@/pages/admin/control-tower/PersonaPage'))
const AdminAccountsPage = lazy(() => import('@/pages/admin/accounts/page'))
const AdminAccountDetailPage = lazy(() => import('@/pages/admin/accounts/[id]/page'))
const NewAccountPage = lazy(() => import('@/pages/admin/accounts/new/page'))
const AdminConversationsPage = lazy(() => import('@/pages/admin/conversations/page'))
const AdminConversationThreadPage = lazy(() => import('@/pages/admin/conversations/[id]/page'))
const AdminSignupsPage = lazy(() => import('@/pages/admin/signups/page'))
const AdminSignupDetailPage = lazy(() => import('@/pages/admin/signups/SignupDetailPage'))
const AdminPlatformInvoicesPage = lazy(() => import('@/pages/admin/platform-invoices/page'))
const AdminPlatformInvoiceDetailPage = lazy(() => import('@/pages/admin/platform-invoices/[id]/page'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/settings/page'))
const AdminHelpdeskPage = lazy(() => import('@/pages/admin/helpdesk/page'))
const AdminStatusPage = lazy(() => import('@/pages/admin/status/page'))
const AdminModelsPage = lazy(() => import('@/pages/admin/models/page'))
const AdminTiersPage = lazy(() => import('@/pages/admin/tiers/page'))
const AdminPortRequestsPage = lazy(() => import('@/pages/admin/port-requests/page'))
const AdminNumbersPage = lazy(() => import('@/pages/admin/numbers/page'))
const AdminDemosPage = lazy(() => import('@/pages/admin/demos/page'))
const AdminVoiceProfilesPage = lazy(() => import('@/pages/admin/voice-profiles/page'))
const AdminPersonaTemplatesPage = lazy(() => import('@/pages/admin/persona-templates/page'))
const AdminReviewsPage = lazy(() => import('@/pages/admin/reviews/page'))
const AdminReviewDetailPage = lazy(() => import('@/pages/admin/reviews/ReviewDetailPage'))
const AdminInvoiceReviewPage = lazy(() => import('@/pages/admin/reviews/InvoiceReviewPage'))
const AdminFormsPage = lazy(() => import('@/pages/admin/forms/page'))
const AdminNeedsYouFeedbackPage = lazy(() => import('@/pages/admin/needs-you-feedback/page'))
const PayInvoicePage = lazy(() => import('@/pages/public/pay/page'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
})

// Home's window scroll per history entry (the phone layout scrolls the window),
// so Back onto Home lands where it was left instead of at the top; Home then
// refocuses the row that was opened.
const homeScroll = new Map<string, number>()

function ScrollToTop() {
  const { pathname, key } = useLocation()
  const navigationType = useNavigationType()
  // Only a new page moves the scroll; a query change on the same page keeps it.
  useEffect(() => {
    const saved = navigationType === 'POP' && pathname === '/home' ? homeScroll.get(key) : undefined
    window.scrollTo({ top: saved ?? 0, behavior: 'instant' as ScrollBehavior })
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pathname !== '/home') return undefined
    const save = () => {
      // The URL changes before the next page renders, so a scroll clamped by
      // that render is not saved as Home's.
      if (window.location.pathname === '/home') homeScroll.set(key, window.scrollY)
    }
    window.addEventListener('scroll', save, { passive: true })
    return () => window.removeEventListener('scroll', save)
  }, [pathname, key])
  return null
}

// Param-forwarding legacy redirects — the queue split into /jobs + /history.
function QueueJobRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/jobs/${id}`} replace />
}

function QueueActivityRedirect() {
  const { kind, id } = useParams<{ kind: string; id: string }>()
  return <Navigate to={`/history/activity/${kind}/${id}`} replace />
}

// Meetings live on the Calendar page now (#536) — the old /meetings URLs
// redirect with their search params intact (a bare <Navigate> would drop
// ?create=meeting and the ?google=connected bounce-back).
function MeetingsRedirect() {
  const { search } = useLocation()
  return <Navigate to={`/calendar/meetings${search}`} replace />
}

function MeetingsDetailRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  return <Navigate to={`/calendar/meetings/${id}${search}`} replace />
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ScrollToTop />
    <ThemeProvider>
    <AuthProvider>
      <ToastProvider>
        {/* Top-level fallback covers public pages + the lazy AdminLayout shell;
            the authenticated/admin layouts carry their own Suspense around
            <Outlet/> so the shell persists during per-page chunk loads. */}
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage />} />
          <Route path="/book/account/:accountId" element={<BookingPage />} />
          <Route path="/book/:token" element={<BookingPage />} />
          <Route path="/meeting-rsvp/:token" element={<MeetingRsvpPage />} />
          {/* Published standalone forms (plans/forms-workspace.md) — public,
              forced-light like /book; the publicId is the credential. */}
          <Route path="/f/:publicId" element={<PublicFormPage />} />
          <Route path="/sample-booking" element={<SampleBookingPage />} />
          <Route path="/sample-setup" element={<SampleSetupPage />} />
          <Route path="/demo/queue/:sessionId" element={<DemoQueuePage />} />
          <Route path="/demo/followup" element={<DemoFollowupPage />} />
          <Route path="/demo/sample-invoice/:sessionId" element={<DemoSampleInvoicePage />} />
          <Route path="/demo/sample-payment/:sessionId" element={<DemoSamplePaymentPage />} />
          <Route path="/pay/success" element={<PaySuccessPage />} />
          <Route path="/pay/cancel" element={<PayCancelPage />} />
          <Route path="/pay/:invoiceId" element={<PayInvoicePage />} />
          {/* Tracked review-link redirect (plans/forms-workspace.md Phase 7) —
              counts the click via the API, then forwards to the platform's
              live review URL; forced-light like /book and /f/. */}
          <Route path="/r/:code" element={<ReviewRedirectPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/aup" element={<AcceptableUsePolicyPage />} />
          <Route path="/dpa" element={<DataProcessingAddendumPage />} />
          <Route path="/sms-demo-consent" element={<SmsDemoConsentPage />} />
          {/* Public customer-subscription pages (MAX retention features) */}
          <Route path="/subscribe/:planId" element={<SubscribePage />} />
          <Route path="/subscribe/:planId/success" element={<SubscribeSuccessPage />} />
          {/* Outreach persona signup form (plans/outreach-persona.md); the
              token-less path serves the same form blank (nothing prefilled) */}
          <Route path="/outreach/signup" element={<OutreachSignupPage />} />
          <Route path="/outreach/signup/:token" element={<OutreachSignupPage />} />

          {/* No /setup route: the /setup wizard is gone, and setup is the
              sidebar checklist now (components/setup/SetupChecklistWidget.tsx),
              with /signup marking the account onboarded. A stale /setup
              bookmark falls through to the catch-all. /setup/business is a
              different page: the conversational business setup (authenticated,
              no layout) that the Home first-run checklist links to. */}
          <Route path="/setup/business" element={<AuthGuard><BusinessSetupPage /></AuthGuard>} />
          {/* Subscription tiers — plan comparison + upgrade entry point.
              Full-page (no sidebar) like the checkout it feeds, per the
              reference in references/tiers/tiers-webapp.mp4: picking a plan is
              a focused decision, not a task inside the app shell. */}
          <Route path="/upgrade" element={<AuthGuard><UpgradePage /></AuthGuard>} />
          {/* Tier checkout — authenticated but full-page (no sidebar), like
              the setup wizard: a Shopify-style two-column checkout. */}
          <Route path="/upgrade/checkout" element={<AuthGuard><UpgradeCheckoutPage /></AuthGuard>} />
          {/* Outside AuthGuard: the page handles both anonymous (?token=xxx
              for password-reset emails) and authenticated (admin-created
              account securing their account) modes. AuthGuard on the rest
              of the app redirects unsecured accounts to this route. */}
          <Route path="/secure-account" element={<SecureAccountPage />} />
          {/* Signup onboarding (profile → business → attribution) — the LAST
              step now that removed /setup, so this page calls finishSetup
              itself. Outside AuthGuard on purpose — the guard's second gate
              would bounce any non-onboarded account straight back here; the
              page enforces its own login/secured/onboarded gates. */}
          <Route path="/signup" element={<SignupPage />} />

          {/* Authenticated */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <AuthenticatedLayout />
              </AuthGuard>
            }
          >
            <Route index element={<Navigate to="/home" replace />} />
            {/* 2026-07 IA cutover: the legacy pages are retired; their URLs
                redirect so old bookmarks/deep links keep working. */}
            <Route path="dashboard" element={<Navigate to="/home" replace />} />
            <Route path="home" element={<HomePage />} />
            <Route path="calls" element={<CallsPage />} />
            <Route path="calls/:id" element={<CallsPage />} />
            <Route path="aria" element={<AriaPage />} />
            <Route path="aria/:conversationId" element={<AriaPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            {/* Meetings live on the Calendar page (#536) — the meetings
                workspace dual-mounts here behind the Calendar | Meetings
                switcher (the messages scope-split pattern). The static
                `meetings` segment has no :id sibling under /calendar, but keep
                the pair declared together. */}
            <Route path="calendar/meetings" element={<MeetingsPage basePath="/calendar/meetings" />} />
            <Route path="calendar/meetings/:id" element={<MeetingsPage basePath="/calendar/meetings" />} />
            <Route path="money" element={<MoneyPage />} />
            <Route path="customers-v2" element={<CustomersV2Page />} />
            {/* Customer detail — dual-mounted like /messages↔/conversations: one
                component, basePath only steers the back link, so both IA
                generations deep-link the same page. */}
            <Route path="customers-v2/:id" element={<CustomerDetailPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage basePath="/customers" />} />
            <Route path="payouts" element={<Navigate to="/money?tab=payouts" replace />} />
            <Route path="settings/overview" element={<SettingsOverviewPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/phone-assistant" element={<RequireAdjustSettings><PhoneAssistantPage /></RequireAdjustSettings>} />
            <Route path="settings/call-handling" element={<RequireAdjustSettings><CallHandlingPage /></RequireAdjustSettings>} />
            <Route path="settings/payments" element={<RequireAdjustSettings><PaymentsPage /></RequireAdjustSettings>} />
            <Route path="settings/invoices" element={<InvoiceSettingsPage />} />
            <Route path="settings/services" element={<RequireAdjustSettings><ServicesPage /></RequireAdjustSettings>} />
            <Route path="settings/memory" element={<RequireAdjustSettings><MemoryPage /></RequireAdjustSettings>} />
            {/* Form BUILDING is admin-only, but an owner reads the responses customers
                submitted to their non-booking forms here (issue #368). */}
            <Route path="settings/forms" element={<Navigate to="/settings" replace />} />
            <Route path="settings/preferences" element={<RequireAdjustSettings><PreferencesPage /></RequireAdjustSettings>} />
            {/* The page outgrew its old route when it took on customer messaging
                and job preferences alongside notifications; the sidebar, the icon
                asset and the page itself all said Preferences while only the URL
                did not. Renamed, and the old path redirects because it is live in
                bookmarks (#631). */}
            <Route path="settings/notifications" element={<Navigate to="/settings/preferences" replace />} />
            <Route path="settings/team" element={<TeamPage />} />
            <Route path="settings/branding" element={<Navigate to="/settings" replace />} />
            <Route path="settings/billing" element={<BillingPage />} />
            <Route path="settings/birthdays" element={<RequireAdjustSettings><BirthdaysPage /></RequireAdjustSettings>} />
            <Route path="settings/reviews" element={<RequireAdjustSettings><ReviewsPage /></RequireAdjustSettings>} />
            <Route path="settings/subscriptions" element={<RequireAdjustSettings><SubscriptionsSettingsPage /></RequireAdjustSettings>} />
            {/* Per-user security (password / 2FA / passkeys) — every signed-in
                user can manage their own, so no RequireAdjustSettings. */}
            <Route path="settings/security" element={<SecurityPage />} />
            <Route path="settings/rates" element={<Navigate to="/settings/services" replace />} />
            <Route path="settings/catalog" element={<Navigate to="/settings/services" replace />} />
            <Route path="settings/tax" element={<Navigate to="/settings/services" replace />} />
            {/* Invoices — the golden-split workspace (2026-08). This is where
                who-owes-you lives; /money's Invoices tab retired into it, and
                the money page forwards ?tab=invoices / ?customer= /
                ?create=invoice here. `:id` is the builder, not a selection. */}
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="queue/generate-invoice/:jobId" element={<GenerateInvoiceRedirect />} />
            {/* Jobs — golden-split workspace; :id preselects a job in the
                master-detail view (react-router reconciles without remount, so
                the index's stage/search/chip state survives). */}
            <Route path="jobs" element={<JobsPage />} />
            <Route path="jobs/:id" element={<JobsPage />} />
            {/* History — the expanded Home "Your history" feed. Reached only
                from the Home card (no sidebar/tab-bar entry on purpose). */}
            <Route path="history" element={<HistoryPage />} />
            <Route path="history/activity/:kind/:id" element={<HistoryPage />} />
            {/* Queue decisions; older job/activity detail URLs keep their redirects. */}
            <Route path="queue" element={<QueuePage />} />
            <Route path="queue/activity/:kind/:id" element={<QueueActivityRedirect />} />
            <Route path="queue/:id" element={<QueueJobRedirect />} />
            {/* New-IA alias of the conversations page — same component, same
                query cache, only its navigations use the /messages prefix. */}
            <Route path="messages" element={<ConversationsPage basePath="/messages" />} />
            {/* Team scope (plans/team-chat-and-meetings.md): internal group
                chats behind the Customers / Ten Four switcher. The static
                `team` segment outranks `:id`, so customer-thread deep links
                are unaffected. */}
            <Route path="messages/team" element={<ConversationsPage basePath="/messages" scope="team" />} />
            <Route path="messages/team/:chatId" element={<ConversationsPage basePath="/messages" scope="team" />} />
            <Route path="messages/:id" element={<ConversationsPage basePath="/messages" />} />
            <Route path="conversations" element={<ConversationsPage />} />
            <Route path="conversations/team" element={<ConversationsPage scope="team" />} />
            <Route path="conversations/team/:chatId" element={<ConversationsPage scope="team" />} />
            {/* Same component as /conversations — the :id just preselects a thread
                in the 3-pane master-detail view (react-router reconciles without
                remount, so list scroll + query cache persist). */}
            <Route path="conversations/:id" element={<ConversationsPage />} />
            {/* Meetings moved under /calendar (#536); the old URLs redirect
                with params preserved (team-chat cards, job detail, ⌘K, RSVP
                links all still say /meetings/:id). */}
            <Route path="meetings" element={<MeetingsRedirect />} />
            <Route path="meetings/:id" element={<MeetingsDetailRedirect />} />
            <Route path="customers" element={<Navigate to="/customers-v2" replace />} />
            {/* Forms workspace — list + golden-split preview; :id is the
                Google-Forms-style builder (plans/forms-workspace.md). */}
            <Route path="forms" element={<RequireAdjustSettings><FormsPage /></RequireAdjustSettings>} />
            <Route path="forms/:id" element={<RequireAdjustSettings><FormBuilderPage /></RequireAdjustSettings>} />
            {/* Revenue → the per-user View reports permission (#572); the server gates the data too. */}
            <Route path="reports" element={<RequireViewReports><ReportsPage /></RequireViewReports>} />
            <Route path="marketing" element={<Navigate to="/customers-v2" replace />} />
            <Route path="notifications" element={<NotificationsListPage />} />
            <Route path="reminders" element={<RemindersPage />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />

          {/* Admin */}
          {/* Same page as /login — only the ADMIN badge and the credential call
              differ (see pages/login/page.tsx). Don't fork it. */}
          <Route path="/admin/login" element={<LoginPage admin />} />
          <Route path="/admin" element={<AdminLayout />}>
            {/* The Control Tower IS the admin home — everything else is secondary. */}
            <Route index element={<Navigate to="/admin/tower" replace />} />
            <Route path="tower" element={<ControlTowerPage />} />
            {/* Persona view — a Control Tower scoped to one persona of one account,
                deep-linked from the Accounts → Personas card. */}
            <Route path="tower/persona/:accountId/:type" element={<PersonaPage />} />
            <Route path="dashboard" element={<AdminDashboardPage />} />
            {/* Review Queue retired → Control Tower */}
            <Route path="queue" element={<Navigate to="/admin/tower" replace />} />
            {/* Inference reviews — per-admin queue + full-context review screen.
                :itemId accepts a review-item id or a legacy log id (the API
                resolves either), so pre-v3 links keep working. The static
                reviews/invoice segment wins over :itemId by specificity —
                the standalone approval page for manual (un-linked) invoices. */}
            <Route path="reviews" element={<AdminReviewsPage />} />
            <Route path="reviews/invoice/:invoiceId" element={<AdminInvoiceReviewPage />} />
            <Route path="reviews/:itemId" element={<AdminReviewDetailPage />} />
            <Route path="accounts" element={<AdminAccountsPage />} />
            <Route path="accounts/new" element={<NewAccountPage />} />
            <Route path="accounts/:id" element={<AdminAccountDetailPage />} />
            <Route path="conversations" element={<AdminConversationsPage />} />
            <Route path="conversations/:id" element={<AdminConversationThreadPage />} />
            {/* Outreach funnel audit (plans/outreach-signups-audit-page.md) */}
            <Route path="signups" element={<AdminSignupsPage />} />
            <Route path="signups/:id" element={<AdminSignupDetailPage />} />
            <Route path="needs-you-feedback" element={<AdminNeedsYouFeedbackPage />} />
            <Route path="platform-invoices" element={<AdminPlatformInvoicesPage />} />
            <Route path="platform-invoices/:id" element={<AdminPlatformInvoiceDetailPage />} />
            <Route path="helpdesk" element={<AdminHelpdeskPage />} />
            <Route path="status" element={<AdminStatusPage />} />
            <Route path="port-requests" element={<AdminPortRequestsPage />} />
            <Route path="models" element={<AdminModelsPage />} />
            <Route path="tiers" element={<AdminTiersPage />} />
            <Route path="numbers" element={<AdminNumbersPage />} />
            <Route path="demos" element={<AdminDemosPage />} />
            <Route path="demos/:accountId" element={<AdminDemosPage />} />
            <Route path="voice-profiles" element={<AdminVoiceProfilesPage />} />
            <Route path="voice-profiles/:name" element={<AdminVoiceProfilesPage />} />
            <Route path="persona-templates" element={<AdminPersonaTemplatesPage />} />
            <Route path="persona-templates/:name" element={<AdminPersonaTemplatesPage />} />
            <Route path="forms" element={<AdminFormsPage />} />
            <Route path="forms/:id" element={<AdminFormsPage />} />
            <Route path="settings/overview" element={<SettingsOverviewPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>
        </Routes>
        </Suspense>
      </ToastProvider>
    </AuthProvider>
    </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
